import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { config } from '../config/env'

// Create axios instance with production-optimized settings
const api: AxiosInstance = axios.create({
  baseURL: config.API_URL,
  timeout: 60000, // 60 seconds for production reliability
  headers: {
    'Content-Type': 'application/json',
  },
  // Add retry configuration for production
  validateStatus: (status) => {
    // Consider 2xx and 3xx as success
    return status >= 200 && status < 400
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const privilegedAccountRequest = /^(?:\/admin\/recruiters|\/recruitment)(?:\/|$)/.test(config.url || '')
    const token = localStorage.getItem(privilegedAccountRequest ? 'resume_access_token' : 'access_token')
    if (privilegedAccountRequest && !token) throw new Error('Please sign out and sign in again to use recruiter management')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // console.log('=== API REQUEST DEBUG ===')
    // console.log('Making API request to:', config.url)
    // console.log('Full URL:', `${config.baseURL ?? ''}${config.url ?? ''}`)
    // console.log('Method:', config.method)
    // console.log('Headers:', config.headers)
    // console.log('Data:', config.data)
    // console.log('=== API REQUEST DEBUG END ===')
    return config
  },
  (error) => {
    console.error('API request error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    // console.log('=== API RESPONSE DEBUG ===')
    // console.log('API response from:', response.config.url)
    // console.log('Status:', response.status)
    // console.log('Response data:', response.data)
    // console.log('=== API RESPONSE DEBUG END ===')
    return response
  },
  async (error) => {
    // console.log('=== API ERROR DEBUG ===')
    // console.log('API error for:', error.config?.url)
    // console.log('Error status:', error.response?.status)
    // console.log('Error data:', error.response?.data)
    // console.log('Error message:', error.message)
    // console.log('=== API ERROR DEBUG END ===')
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const refreshTokenValue = localStorage.getItem('refresh_token')
      if (refreshTokenValue) {
        try {
          const response = await axios.post('/api/v1/auth/refresh', {
            refresh_token: refreshTokenValue,
          })
          
          const { access_token, refresh_token: newRefreshToken } = response.data
          localStorage.setItem('access_token', access_token)
          localStorage.setItem('refresh_token', newRefreshToken)
          
          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          return api(originalRequest)
        } catch (refreshError) {
          // Clear tokens and redirect to login
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          window.location.href = '/login'
          return Promise.reject(refreshError)
        }
      } else {
        // Clear tokens and redirect to login
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login: (email: string, password: string) => {
    // Use URLSearchParams for proper form encoding
    const formData = new URLSearchParams()
    formData.append('username', email)
    formData.append('password', password)
    
    return api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
  },
  
  register: (userData: {
    email: string
    password: string
    first_name: string
    last_name: string
    phone_number?: string
    role?: string
  }) => api.post('/auth/register', userData),
  
  getCurrentUser: () => api.get('/auth/me'),
  
  refreshToken: (refreshToken: string) => 
    api.post('/auth/refresh', { refresh_token: refreshToken }),
  
  logout: () => api.post('/auth/logout'),
}

// Test/Session API
export const testAPI = {
  // Session management
  createSession: (sessionData: { 
    assessment_id: number | string; 
    questions?: any[];
    duration_minutes?: number;
    assessment_type?: string;
    candidate_email?: string;
  }, browserInfo?: any) =>
    api.post('/sessions/create', {
      ...sessionData,
      browser_info: browserInfo,
      ip_address: window.location.hostname,
    }),
  
  startSession: (sessionId: string, proctorSessionId?: string) => {
    //console.log(`[API] Starting session ${sessionId}...`)
    const startTime = Date.now()
    
    return api.post(`/sessions/${sessionId}/start`, {
      proctoring_session_id: proctorSessionId,
      device_check_passed: true,
      environment_check_passed: true,
    }).then(response => {
      const duration = Date.now() - startTime
      //console.log(`[API] Session started successfully in ${duration}ms`, response.data)
      return response
    }).catch(error => {
      const duration = Date.now() - startTime
      console.error(`[API] Failed to start session after ${duration}ms:`, error)
      throw error
    })
  },
  
  getSessionStatus: (sessionId: string) =>
    api.get(`/sessions/${sessionId}/status`, { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } }),
  
  syncTimer: (sessionId: string) =>
    api.get(`/sessions/${sessionId}/sync`),
  
  pauseSession: (sessionId: string, reason: string) =>
    api.post(`/sessions/${sessionId}/pause`, { reason }),
  
  resumeSession: (sessionId: string) =>
    api.post(`/sessions/${sessionId}/resume`),
  
  submitSession: (sessionId: string, submissionReason = 'manual') =>
    api.post(`/sessions/${sessionId}/submit`, { final_check: true, submission_reason: submissionReason }),
  
  reconnectSession: (sessionId: string) =>
    api.post(`/sessions/${sessionId}/reconnect`),
  
  // Question responses
  saveResponse: (sessionId: string, questionId: number, responseData: any) =>
    api.post('/responses/save', {
      session_id: sessionId,
      question_id: questionId,
      response_data: responseData,
    }),
  
  getResponse: (sessionId: string, questionId: number) =>
    api.get(`/responses/${sessionId}/${questionId}`),
  
  getAllResponses: (sessionId: string) =>
    api.get(`/responses/session/${sessionId}`),
}

// Assessment Responses API (New simplified system)
export const assessmentAPI = {
  // Save a single question response
  saveQuestionResponse: (assessmentId: string, data: {
    candidate_id: number
    drive_id: number
    question_id: string
    selected_option: string
  }) => api.post(`/assessments/${assessmentId}/responses`, data),

  // Generate questions dynamically from question bank (candidate flow)
  generateQuestions: (driveId: string, assessmentType: string, numQuestions: number, durationMinutes: number) =>
    api.post('/assessments/generate-questions', {
      drive_id: driveId,
      assessment_type: assessmentType,
      num_questions: numQuestions,
      duration_minutes: durationMinutes
    }),
  
  // Get assessment results for a candidate
  getAssessmentResults: (assessmentId: string, candidateId: number, driveId?: number) => {
    const params = driveId ? `?drive_id=${driveId}` : ''
    return api.get(`/assessments/${assessmentId}/results/${candidateId}${params}`)
  },
  
  // Get all drive results for a candidate
  getCandidateDriveResults: (candidateId: number, driveId: number) =>
    api.get(`/candidates/${candidateId}/drives/${driveId}/results`),
  
  // Submit an assessment
  submitAssessment: (assessmentId: string, data: {
    candidate_id: number
    drive_id: number
    remaining_time_seconds?: number
    total_duration_minutes?: number
    submission_reason?: 'manual' | 'timer_expired' | 'tab_limit'
  }) => api.post(`/assessments/${assessmentId}/submit`, data),
  
  // Submit a coding solution (runs against hidden test cases)
  submitCodingSolution: (assessmentId: string, data: {
    candidate_id: number
    drive_id: number
    question_id: string
    source_code: string
    language: string
    execution_result?: any  // Optional: pass execution result from Run Code to avoid re-execution
  }) => api.post(`/assessments/${assessmentId}/submit-coding`, data),
  
  // Get completed assessment IDs for a candidate (from MySQL)
  getCompletedAssessments: (candidateEmail: string) => 
    api.get(`/candidate/completed-assessments?candidate_email=${encodeURIComponent(candidateEmail)}`),
}

// Sandbox API
export const sandboxAPI = {
  executeCode: (data: {
    session_id: string
    question_id: number
    source_code: string
    language: string
    time_limit?: number
    memory_limit?: number
  }) => api.post('/sandbox/execute', data),
  
  validateSyntax: (data: {
    source_code: string
    language: string
  }) => api.post('/sandbox/validate', data),
  
  getSupportedLanguages: () => api.get('/sandbox/languages'),
  
  testConnection: () => api.post('/sandbox/test-connection'),
  
  healthCheck: () => api.get('/sandbox/health'),
}

// Question Pool API
export const questionPoolAPI = {
  getRandomQuestions: (data: {
    college_type: string
    tier: string
    role?: string
    categories: string[]
    num_questions_per_category: number
    difficulty_distribution?: any
  }) => api.post('/question-pools/random-questions', data),
  
  getQuestionBank: (params?: {
    category?: string
    question_type?: string
    subcategory?: string
    difficulty_level?: string
    is_verified?: boolean
    limit?: number
    offset?: number
  }) => api.get('/question-pools/question-bank', { params }),
  
  createQuestion: (data: any) => api.post('/question-pools/question-bank', data),
  
  updateQuestion: (questionId: number, data: any) => api.put(`/question-pools/question-bank/${questionId}`, data),
  
  deleteQuestion: (questionId: number) => api.delete(`/question-pools/question-bank/${questionId}`),
  
  bulkCreateQuestions: (data: { questions: any[] }) => api.post('/question-pools/question-bank/bulk', data),
}

// Assessment Management API
export const assessmentManagementAPI = {
  getAssessments: (page?: number, limit?: number) =>
    api.get('/assessments', { params: { page, limit } }),
  
  getAssessment: (id: number) =>
    api.get(`/assessments/${id}`),
  
  getAssessmentQuestions: (id: number) =>
    api.get(`/assessments/${id}/questions`),

  createAssessment: (data: any) =>
    api.post('/assessments', data),

  updateAssessment: (id: number, data: any) =>
    api.put(`/assessments/${id}`, data),

  deleteAssessment: (id: number) =>
    api.delete(`/assessments/${id}`),
  
  // Generate questions dynamically from question bank
  generateQuestions: (driveId: string, assessmentType: string, numQuestions: number, durationMinutes: number) =>
    api.post('/assessments/generate-questions', {
      drive_id: driveId,
      assessment_type: assessmentType,
      num_questions: numQuestions,
      duration_minutes: durationMinutes
    }),
}

// Analytics API
export const analyticsAPI = {
  getPerformanceMetrics: (sessionId: string) =>
    api.get(`/analytics/performance/${sessionId}`),
  
  getCohortAnalytics: (assessmentId: number) =>
    api.get(`/analytics/cohort/${assessmentId}`),
}

// Question Bank API
export const questionBankAPI = {
  // Get questions with filters
  getQuestions: (params?: {
    category?: string
    question_type?: string
    subcategory?: string
    difficulty_level?: string
    is_verified?: boolean
    limit?: number
    offset?: number
  }) => api.get('/question-pools/question-bank', { params }),
  
  // Get specific question
  getQuestion: (id: number) => api.get(`/question-pools/question-bank/${id}`),
  
  // Create new question
  createQuestion: (questionData: any) => api.post('/question-pools/question-bank', questionData),
  
  // Update question
  updateQuestion: (id: number, questionData: any) => 
    api.put(`/question-pools/question-bank/${id}`, questionData),
  
  // Delete question
  deleteQuestion: (id: number) => api.delete(`/question-pools/question-bank/${id}`),
  
  // Search questions
  searchQuestions: (searchTerm: string, params?: {
    category?: string
    question_type?: string
    limit?: number
  }) => api.get('/question-pools/question-bank/search', {
    params: { q: searchTerm, ...params }
  }),
  
  // Get category statistics
  getCategoryStats: (category: string) => 
    api.get(`/question-pools/question-bank/stats/category/${category}`),
  
  // Bulk upload questions
  bulkCreateQuestions: (questions: any[]) => 
    api.post('/question-pools/question-bank/bulk', { questions }),
}

// Coding Questions API (HackerRank/LeetCode style)
export const codingQuestionsAPI = {
  // Create coding question
  createCodingQuestion: (questionData: any) => 
    api.post('/coding/coding-questions', questionData),
  
  // List coding questions
  getCodingQuestions: (params?: {
    category?: string
    subcategory?: string
    difficulty_level?: string
    tags?: string
    is_verified?: boolean
    limit?: number
    offset?: number
  }) => api.get('/coding/coding-questions', { params }),
  
  // Get specific coding question (admin/recruiter view - includes hidden test cases)
  getCodingQuestion: (id: number) => 
    api.get(`/coding/coding-questions/${id}`),
  
  // Get coding question for candidate (excludes hidden test cases)
  getCodingQuestionCandidateView: (id: number) => 
    api.get(`/coding/coding-questions/${id}/candidate-view`),
  
  // Update coding question
  updateCodingQuestion: (id: number, questionData: any) => 
    api.put(`/coding/coding-questions/${id}`, questionData),
  
  // Delete coding question
  deleteCodingQuestion: (id: number) => 
    api.delete(`/coding/coding-questions/${id}`),
  
  // Get statistics
  getCodingQuestionStats: (category?: string) => 
    api.get('/coding/coding-questions/stats/overview', { params: { category } }),
  
  // Get supported data types
  getSupportedDataTypes: () => 
    api.get('/coding/data-types'),
}

// Proctoring API
export const proctoringAPI = {
  startProctoring: (sessionId: string) =>
    api.post('/proctoring/start', { session_id: sessionId }),
  
  uploadVerificationImage: (file: File, verificationType: string) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('verification_type', verificationType)
    
    return api.post('/proctoring/verify-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  
  reportEvent: (sessionId: string, eventType: string, eventData: any) =>
    api.post('/proctoring/event', {
      session_id: sessionId,
      event_type: eventType,
      event_data: eventData,
    }),
}

// Drive API
export const driveAPI = {
  // Create a new drive
  createDrive: (driveData: {
    title: string
    description?: string
    college_type: 'engineering' | 'management'
    tier: 'tier1' | 'tier2'
    role?: string
    start_date: string
    end_date: string
    max_candidates: number
    instructions?: string
    assessment_configs: any
  }) => api.post('/drives/', driveData),
  
  // Get all drives (for recruiters: their drives, for candidates: active drives)
  getDrives: (params?: {
    skip?: number
    limit?: number
    college_type?: string
    tier?: string
    status?: string
  }) => api.get('/drives/', { params }),
  
  // Get drive by ID
  getDrive: (driveId: number) => api.get(`/drives/${driveId}`),
  
  // Get drive by registration link
  getDriveByLink: (registrationLink: string) => 
    api.get(`/drives/link/${registrationLink}`),
  
  // Update drive
  updateDrive: (driveId: number, driveData: any) => 
    api.put(`/drives/${driveId}`, driveData),
  
  // Delete drive
  deleteDrive: (driveId: number) => api.delete(`/drives/${driveId}`),
  
  // Register for a drive (candidates)
  registerForDrive: (data: {
    registration_link: string
    registration_data?: any
  }) => api.post('/drives/register', data),
  
  // Get registrations for a drive (recruiters)
  getDriveRegistrations: (driveId: number, params?: {
    skip?: number
    limit?: number
  }) => api.get(`/drives/${driveId}/registrations`, { params }),
  
  // Get candidate's own registrations
  getMyRegistrations: (candidateEmail: string, params?: {
    skip?: number
    limit?: number
  }) => api.get('/candidate/drives', { 
    params: { 
      candidate_email: candidateEmail,
      ...params 
    } 
  }),
}

// Question Management API (Admin / MongoDB)
export const questionManagementAPI = {
  getStats: () => api.get('/questions/stats'),
  
  getQuestions: (params?: {
    question_type?: string
    category?: string
    difficulty_level?: string
    search?: string
    page?: number
    per_page?: number
  }) => api.get('/questions', { params }),
  
  getQuestion: (id: string) => api.get(`/questions/${id}`),
  
  createQuestion: (data: any) => api.post('/questions', data),
  
  updateQuestion: (id: string, data: any) => api.put(`/questions/${id}`, data),
  
  deleteQuestion: (id: string) => api.delete(`/questions/${id}`),
  
  bulkUpload: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/questions/bulk-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
}

export default api
